import { v4 as uuidv4 } from "uuid";

import type { CargoItem } from "@features/services/shipping/types";

const CACHE_KEY = "elk-cargo-extraction-cache";
const MAX_CACHE_ENTRIES = 50;

interface CacheEntry {
  hash: string;
  items: CargoItem[];
  timestamp: number;
}

export async function hashFile(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function readCache(): CacheEntry[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw == null) return [];
    return JSON.parse(raw) as CacheEntry[];
  } catch {
    return [];
  }
}

function writeCache(entries: CacheEntry[]): void {
  const sorted = entries.sort((a, b) => b.timestamp - a.timestamp);
  const trimmed = sorted.slice(0, MAX_CACHE_ENTRIES);
  localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
}

export function getCachedResult(hash: string): CargoItem[] | null {
  const entries = readCache();
  const entry = entries.find((e) => e.hash === hash);
  if (entry == null) return null;
  return entry.items.map((item) => ({ ...item, id: uuidv4() }));
}

export function setCachedResult(hash: string, items: CargoItem[]): void {
  const entries = readCache();
  const filtered = entries.filter((e) => e.hash !== hash);
  filtered.push({ hash, items, timestamp: Date.now() });
  writeCache(filtered);
}
